// Ligação da API ao React: <ApiProvider> (cliente da API + cliente das contas) + hooks de dados partilhados.
// Os hooks usam TanStack Query: no servidor (pré-renderização) não fazem pedidos, por isso o HTML sai no estado
// "a carregar" e o browser preenche-o — sem diferenças na hidratação.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, use, useContext, useState, type ReactNode } from "react";
import type { AuthClient } from "./auth";
import type { ApiClient } from "./index";

type Ctx = { api: ApiClient; loadAuth: () => Promise<AuthClient> };
const ApiContext = createContext<Ctx | null>(null);

/**
 * O cliente das contas (Better Auth) só é descarregado quando é preciso — páginas de conta, login, sair —
 * e fica guardado. As páginas públicas não pagam esse peso.
 */
export function ApiProvider({ client, authUrl, children }: { client: ApiClient; authUrl: string; children: ReactNode }) {
  const [loadAuth] = useState(() => {
    let p: Promise<AuthClient> | null = null;
    return () => (p ??= import("./auth").then((m) => m.createEclipticaAuthClient(authUrl)));
  });
  return <ApiContext.Provider value={{ api: client, loadAuth }}>{children}</ApiContext.Provider>;
}

function useCtx() {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error("useApi()/useAuthClient() used outside <ApiProvider>");
  return ctx;
}

export function useApi(): ApiClient {
  return useCtx().api;
}

/** Cliente das contas para usar a renderizar (suspende até estar carregado — pôr dentro de <Suspense>). */
export function useAuthClient(): AuthClient {
  return use(useCtx().loadAuth());
}

/** Carregador do cliente das contas para usar em eventos (ex.: botão "Sair"), sem suspender a renderização. */
export function useAuthLoader(): () => Promise<AuthClient> {
  return useCtx().loadAuth;
}

/** Estado da API e da base de dados (atualiza a cada 30 s). */
export function useHealth() {
  const api = useApi();
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const res = await api.v1.health.$get();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    refetchInterval: 30_000,
    retry: 1,
  });
}

/** Informação pública: fornecedores de login ativos, registo aberto, manutenção. */
export function usePublicInfo() {
  const api = useApi();
  return useQuery({
    queryKey: ["public-info"],
    queryFn: async () => {
      const res = await api.v1.public.info.$get();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 5 * 60_000,
  });
}

type MeResponse = Awaited<ReturnType<Awaited<ReturnType<ApiClient["v1"]["me"]["$get"]>>["json"]>>;
export type Me = Extract<MeResponse, { user: { id: string } }>;

/** Quem está ligado (null = ninguém). Inclui papel e permissões. */
export function useMe() {
  const api = useApi();
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const res = await api.v1.me.$get();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      return body.user ? (body as Me) : null;
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Depois de entrar, sair ou mudar a conta: volta a pedir /v1/me. */
export function useRefreshMe() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["me"] });
}
